import React, { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ArrowUp,
  Ban,
  Bell,
  BookmarkCheck,
  CalendarClock,
  CheckCircle2,
  ChevronsUp,
  Clock3,
  ExternalLink,
  FileCheck2,
  FilterX,
  Gavel,
  Eye,
  EyeOff,
  Loader2,
  Minus,
  Plus,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Tag,
  X,
  Zap,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { settingsService } from '../services/settingsService';
import {
  AppSettings,
  ParticipationDecision,
  Tender,
  TenderPriority,
  TenderStatus,
  TenderWorkflowStage,
} from '../types';
import { useModal } from './ModalProvider';

type WorkflowMode = 'FAVORITES' | 'ONGOING';

interface TenderWorkflowProps {
  mode: WorkflowMode;
}

const PRIORITIES: Array<{
  value: TenderPriority;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: string;
}> = [
  { value: 'NONE', label: 'Sem prioridade', icon: Minus, active: 'border-line-strong bg-surface-alt text-ink-title dark:border-line-dark-strong dark:bg-surface-dark-alt dark:text-ink-title-dark' },
  { value: 'ULTRA', label: 'Ultra prioridade', icon: Zap, active: 'border-red-500 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300' },
  { value: 'VERY_HIGH', label: 'Altíssima prioridade', icon: ChevronsUp, active: 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300' },
  { value: 'PRIORITY', label: 'Prioridade', icon: ArrowUp, active: 'border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300' },
  { value: 'NO_GO', label: 'Não participar', icon: Ban, active: 'border-zinc-500 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200' },
];

type QualificationCheck =
  | 'documentsSeparated'
  | 'documentsValidated'
  | 'brandsValidated'
  | 'modelsValidated'
  | 'valuesValidated';

const CHECKS: Array<{ field: QualificationCheck; label: string }> = [
  { field: 'documentsSeparated', label: 'Documentação separada' },
  { field: 'documentsValidated', label: 'Documentação validada' },
  { field: 'brandsValidated', label: 'Marcas validadas' },
  { field: 'modelsValidated', label: 'Modelos validados' },
  { field: 'valuesValidated', label: 'Valores validados' },
];

const formatDate = (value?: string | null) => {
  if (!value) return 'Não informado';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatMoney = (value?: number) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BRAZIL_STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

type OpportunityContractType = 'LEASE' | 'ACQUISITION' | 'BOTH' | 'UNKNOWN';

const CONTRACT_TYPE_META: Record<OpportunityContractType, { label: string; className: string }> = {
  LEASE: { label: 'Locação', className: 'border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300' },
  ACQUISITION: { label: 'Aquisição', className: 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-300' },
  BOTH: { label: 'Locação e aquisição', className: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300' },
  UNKNOWN: { label: 'Não identificado', className: 'border-line dark:border-line-dark bg-surface-alt text-ink-subtitle dark:bg-surface-dark-alt dark:text-ink-subtitle-dark' },
};

const normalizeOpportunityText = (value: unknown) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR');

const classifyOpportunityContract = (tender: Tender): OpportunityContractType => {
  const text = normalizeOpportunityText([tender.title, tender.description, tender.sourceObject].filter(Boolean).join(' '));
  const hasLease = [
    'locacao', 'aluguel', 'comodato', 'licenca mensal', 'licenca anual',
    'licenca locada', 'licenca recorrente', 'subscricao', 'assinatura de software',
    'software como servico', 'saas',
  ].some((term) => text.includes(term));
  const hasAcquisition = [
    'aquisicao', 'compra de equipamento', 'compra de software', 'fornecimento de equipamento',
    'fornecimento de hardware', 'licenca perpetua', 'licenca vitalicia', 'licenca definitiva',
    'direito de uso permanente',
  ].some((term) => text.includes(term));

  if (hasLease && hasAcquisition) return 'BOTH';
  if (hasLease) return 'LEASE';
  if (hasAcquisition) return 'ACQUISITION';
  return 'UNKNOWN';
};

const startOfDay = (value: Date) => {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
};

const getDaysRemaining = (value?: string | null) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((startOfDay(target).getTime() - startOfDay(new Date()).getTime()) / 86_400_000);
};

const deadlineLabel = (days: number | null) => {
  if (days === null) return 'Prazo não informado';
  if (days < 0) return `Encerrada há ${Math.abs(days)} dia${Math.abs(days) === 1 ? '' : 's'}`;
  if (days === 0) return 'Encerra hoje';
  if (days === 1) return '1 dia restante';
  return `${days} dias restantes`;
};

const deadlineBadgeClass = (days: number | null) => {
  if (days === null) return 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark';
  if (days < 0) return 'border-zinc-400 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200';
  if (days <= 3) return 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300';
  if (days <= 7) return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300';
  return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300';
};

const isReady = (tender: Tender) =>
  tender.priority !== 'NONE'
  && tender.priority !== 'NO_GO'
  && tender.participationDecision === 'PARTICIPATE'
  && CHECKS.every(({ field }) => Boolean(tender[field]));

const TenderWorkflow: React.FC<TenderWorkflowProps> = ({ mode }) => {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TenderPriority | 'ALL'>('ALL');
  const [participationFilter, setParticipationFilter] = useState<ParticipationDecision | 'ALL'>('ALL');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [deadlineWindow, setDeadlineWindow] = useState<'ALL' | 'TODAY' | '3' | '7' | '15' | '30' | 'OVERDUE'>('ALL');
  const [deadlineStart, setDeadlineStart] = useState('');
  const [deadlineEnd, setDeadlineEnd] = useState('');
  const [readinessFilter, setReadinessFilter] = useState<'ALL' | 'READY' | 'PENDING'>('ALL');
  const [sortBy, setSortBy] = useState<'DEADLINE' | 'PRIORITY' | 'RECENT'>('DEADLINE');
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [notificationForm, setNotificationForm] = useState({
    emailRecipients: '',
    notifyDeadlineWarning: true,
    notifyStatusChange: true,
  });
  const [manualForm, setManualForm] = useState({
    title: '',
    organ: '',
    portal: '',
    state: '',
    publicationDate: new Date().toISOString().slice(0, 10),
    deadline: '',
    value: '',
    description: '',
    priority: 'NONE' as TenderPriority,
  });
  const { showAlert, showConfirm } = useModal();
  const stage: TenderWorkflowStage = mode === 'FAVORITES' ? 'FAVORITED' : 'ONGOING';

  const loadTenders = async () => {
    try {
      setLoading(true);
      setTenders(await apiService.getWorkflowTenders(stage));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao carregar licitações.', 'Erro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTenders();
  }, [stage]);

  const visibleTenders = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    const priorityWeight: Record<TenderPriority, number> = {
      ULTRA: 0,
      VERY_HIGH: 1,
      PRIORITY: 2,
      NONE: 3,
      NO_GO: 4,
    };
    const start = deadlineStart ? startOfDay(new Date(`${deadlineStart}T00:00:00`)).getTime() : null;
    const end = deadlineEnd ? startOfDay(new Date(`${deadlineEnd}T00:00:00`)).getTime() : null;

    return tenders
      .filter((tender) => {
        if (term && ![tender.title, tender.organ, tender.state, tender.portal]
          .some((value) => String(value || '').toLocaleLowerCase('pt-BR').includes(term))) return false;
        if (priorityFilter !== 'ALL' && tender.priority !== priorityFilter) return false;
        if (participationFilter !== 'ALL' && tender.participationDecision !== participationFilter) return false;
        if (stateFilter !== 'ALL' && tender.state !== stateFilter) return false;
        if (readinessFilter === 'READY' && !isReady(tender)) return false;
        if (readinessFilter === 'PENDING' && isReady(tender)) return false;

        const days = getDaysRemaining(tender.deadline);
        if (deadlineWindow === 'TODAY' && days !== 0) return false;
        if (deadlineWindow === 'OVERDUE' && (days === null || days >= 0)) return false;
        if (!['ALL', 'TODAY', 'OVERDUE'].includes(deadlineWindow)) {
          const maximum = Number(deadlineWindow);
          if (days === null || days < 0 || days > maximum) return false;
        }

        const tenderDate = startOfDay(new Date(tender.deadline)).getTime();
        if (start !== null && tenderDate < start) return false;
        if (end !== null && tenderDate > end) return false;
        return true;
      })
      .sort((left, right) => {
        if (sortBy === 'PRIORITY') {
          const priorityDifference = priorityWeight[left.priority || 'NONE'] - priorityWeight[right.priority || 'NONE'];
          if (priorityDifference !== 0) return priorityDifference;
        }
        if (sortBy === 'RECENT') {
          return new Date(right.ongoingAt || 0).getTime() - new Date(left.ongoingAt || 0).getTime();
        }
        return new Date(left.deadline).getTime() - new Date(right.deadline).getTime();
      });
  }, [
    deadlineEnd,
    deadlineStart,
    deadlineWindow,
    priorityFilter,
    participationFilter,
    readinessFilter,
    search,
    sortBy,
    stateFilter,
    tenders,
  ]);

  const hasActiveFilters = priorityFilter !== 'ALL'
    || participationFilter !== 'ALL'
    || stateFilter !== 'ALL'
    || deadlineWindow !== 'ALL'
    || Boolean(deadlineStart)
    || Boolean(deadlineEnd)
    || readinessFilter !== 'ALL'
    || sortBy !== 'DEADLINE';

  const clearFilters = () => {
    setPriorityFilter('ALL');
    setParticipationFilter('ALL');
    setStateFilter('ALL');
    setDeadlineWindow('ALL');
    setDeadlineStart('');
    setDeadlineEnd('');
    setReadinessFilter('ALL');
    setSortBy('DEADLINE');
  };

  const openNotificationSettings = async () => {
    const stored = await settingsService.getAppSettings();
    setAppSettings(stored);
    setNotificationForm({
      emailRecipients: stored?.emailRecipients || '',
      notifyDeadlineWarning: stored?.notifyDeadlineWarning ?? true,
      notifyStatusChange: stored?.notifyStatusChange ?? true,
    });
    setIsNotificationModalOpen(true);
  };

  const saveNotificationSettings = async () => {
    try {
      setSavingNotifications(true);
      await settingsService.saveAppSettings({
        aiProvider: appSettings?.aiProvider || 'GEMINI',
        ...appSettings,
        ...notificationForm,
      });
      setIsNotificationModalOpen(false);
      await showAlert('Configuração de notificações salva.', 'Alertas atualizados');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao salvar notificações.', 'Erro');
    } finally {
      setSavingNotifications(false);
    }
  };

  const saveManualTender = async () => {
    if (!manualForm.title.trim() || !manualForm.organ.trim() || !manualForm.deadline) {
      await showAlert('Preencha objeto, órgão e prazo da licitação.', 'Campos obrigatórios');
      return;
    }
    try {
      setSavingManual(true);
      const created = await apiService.createTender({
        title: manualForm.title.trim(),
        organ: manualForm.organ.trim(),
        portal: manualForm.portal.trim(),
        state: manualForm.state,
        publicationDate: manualForm.publicationDate ? `${manualForm.publicationDate}T12:00:00` : undefined,
        deadline: `${manualForm.deadline}T12:00:00`,
        value: Number(manualForm.value || 0),
        description: manualForm.description.trim(),
        status: TenderStatus.ANALYSIS,
        workflowStage: 'ONGOING',
        priority: manualForm.priority,
        participationDecision: 'PENDING',
        isRegistered: false,
        areDocsReady: false,
      });
      setTenders((current) => [created, ...current]);
      setManualForm({
        title: '',
        organ: '',
        portal: '',
        state: '',
        publicationDate: new Date().toISOString().slice(0, 10),
        deadline: '',
        value: '',
        description: '',
        priority: 'NONE',
      });
      setIsManualModalOpen(false);
      await showAlert('Oportunidade inserida em Licitações em andamento.', 'Cadastro concluído');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao cadastrar licitação.', 'Erro');
    } finally {
      setSavingManual(false);
    }
  };

  const updateQualification = async (
    tender: Tender,
    changes: Parameters<typeof apiService.updateTenderQualification>[1],
  ) => {
    try {
      setWorkingId(tender.id);
      const updated = await apiService.updateTenderQualification(tender.id, changes);
      if (updated.workflowStage !== stage) {
        setTenders((current) => current.filter((item) => item.id !== tender.id));
      } else {
        setTenders((current) => current.map((item) => item.id === tender.id ? { ...item, ...updated } : item));
      }
      return updated;
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao atualizar a licitação.', 'Erro');
      return null;
    } finally {
      setWorkingId(null);
    }
  };

  const advance = async (tender: Tender) => {
    try {
      setWorkingId(tender.id);
      const updated = await apiService.advanceTenderToParticipation(tender.id);
      setTenders((current) => current.map((item) => item.id === tender.id ? { ...item, ...updated } : item));
      window.location.hash = `/participation?opportunity=${encodeURIComponent(tender.id)}&tab=AUCTION`;
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível avançar.', 'Pendências');
    } finally {
      setWorkingId(null);
    }
  };

  const openAuctionRoom = (tender: Tender) => {
    window.location.hash = `/participation?opportunity=${encodeURIComponent(tender.id)}&tab=AUCTION`;
  };

  const archiveTender = async (tender: Tender) => {
    const confirmed = await showConfirm(
      `Arquivar o card de “${tender.title}”? O processo, documentos e histórico continuarão disponíveis em Resultados.`,
      'Arquivar processo',
    );
    if (!confirmed) return;
    try {
      setWorkingId(tender.id);
      await apiService.archiveTender(tender.id);
      setTenders((current) => current.filter((item) => item.id !== tender.id));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao arquivar o processo.', 'Erro');
    } finally {
      setWorkingId(null);
    }
  };

  const toggleResultTracking = async (tender: Tender) => {
    try {
      setWorkingId(tender.id);
      const updated = await apiService.setTenderResultTracking(tender.id, !tender.trackResult);
      setTenders((current) => current.map((item) => item.id === tender.id ? { ...item, ...updated } : item));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao atualizar o acompanhamento.', 'Erro');
    } finally {
      setWorkingId(null);
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-sm text-ink-subtitle"><Loader2 className="w-4 h-4 animate-spin" />Carregando licitações...</div>;
  }

  return (
    <div className="max-w-[1480px] mx-auto px-4 sm:px-6 py-5 space-y-4">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {mode === 'FAVORITES' ? <BookmarkCheck className="w-5 h-5 text-brand-600" /> : <Clock3 className="w-5 h-5 text-brand-600" />}
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark">
              {mode === 'FAVORITES' ? 'Licitações em Participação' : 'Licitações em andamento'}
            </h2>
          </div>
          <p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
            {mode === 'FAVORITES'
              ? 'Classifique cada oportunidade. As três prioridades de participação seguem automaticamente para Em andamento.'
              : 'Confirme a decisão e valide a preparação antes de liberar o processo para Participação.'}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <div className="relative min-w-0 flex-1 lg:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-placeholder" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por objeto, órgão, UF ou portal"
              className="input w-full pl-9"
            />
          </div>
          {mode === 'ONGOING' && (
            <>
              <button
                type="button"
                onClick={() => void openNotificationSettings()}
                title="Configurar notificações"
                aria-label="Configurar notificações"
                className="w-10 h-10 shrink-0 rounded-md border border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center justify-center"
              >
                <Bell className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsManualModalOpen(true)}
                title="Adicionar oportunidade manualmente"
                aria-label="Adicionar oportunidade manualmente"
                className="w-10 h-10 shrink-0 rounded-md bg-brand-600 hover:bg-brand-700 text-white flex items-center justify-center"
              >
                <Plus className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </header>

      {mode === 'ONGOING' && (
        <section className="border-y border-line dark:border-line-dark py-3">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(6,minmax(0,1fr))_auto] gap-2">
            <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value as TenderPriority | 'ALL')} className="input h-10 text-sm" aria-label="Filtrar por prioridade">
              <option value="ALL">Todas as prioridades</option>
              <option value="ULTRA">Ultra prioridade</option>
              <option value="VERY_HIGH">Altíssima prioridade</option>
              <option value="PRIORITY">Prioridade</option>
              <option value="NONE">Sem prioridade</option>
              <option value="NO_GO">Não participar</option>
            </select>
            <select value={participationFilter} onChange={(event) => setParticipationFilter(event.target.value as ParticipationDecision | 'ALL')} className="input h-10 text-sm" aria-label="Filtrar por decisão de participação">
              <option value="ALL">Todas as decisões</option>
              <option value="PARTICIPATE">Participar</option>
              <option value="DO_NOT_PARTICIPATE">Não participar</option>
              <option value="PENDING">Decisão pendente</option>
            </select>
            <select value={stateFilter} onChange={(event) => setStateFilter(event.target.value)} className="input h-10 text-sm" aria-label="Filtrar por UF">
              <option value="ALL">Todas as UFs</option>
              {BRAZIL_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
            <select value={deadlineWindow} onChange={(event) => setDeadlineWindow(event.target.value as typeof deadlineWindow)} className="input h-10 text-sm" aria-label="Filtrar por prazo">
              <option value="ALL">Todos os prazos</option>
              <option value="TODAY">Encerra hoje</option>
              <option value="3">Próximos 3 dias</option>
              <option value="7">Próximos 7 dias</option>
              <option value="15">Próximos 15 dias</option>
              <option value="30">Próximos 30 dias</option>
              <option value="OVERDUE">Prazo encerrado</option>
            </select>
            <select value={readinessFilter} onChange={(event) => setReadinessFilter(event.target.value as typeof readinessFilter)} className="input h-10 text-sm" aria-label="Filtrar por preparação">
              <option value="ALL">Toda preparação</option>
              <option value="READY">Preparação concluída</option>
              <option value="PENDING">Com pendências</option>
            </select>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)} className="input h-10 text-sm" aria-label="Ordenar licitações">
              <option value="DEADLINE">Prazo mais próximo</option>
              <option value="PRIORITY">Maior prioridade</option>
              <option value="RECENT">Adicionadas recentemente</option>
            </select>
            <button
              type="button"
              disabled={!hasActiveFilters}
              onClick={clearFilters}
              title="Limpar filtros"
              aria-label="Limpar filtros"
              className="w-10 h-10 rounded-md border border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt disabled:opacity-40 flex items-center justify-center"
            >
              <FilterX className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2 max-w-xl">
            <label className="flex items-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
              <span className="w-16">Prazo de</span>
              <input type="date" value={deadlineStart} onChange={(event) => setDeadlineStart(event.target.value)} className="input h-9 flex-1 text-sm" />
            </label>
            <label className="flex items-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
              <span className="w-16">Prazo até</span>
              <input type="date" value={deadlineEnd} onChange={(event) => setDeadlineEnd(event.target.value)} className="input h-9 flex-1 text-sm" />
            </label>
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-line dark:border-line-dark py-3 text-sm">
        <span className="font-semibold text-ink-title dark:text-ink-title-dark">
          {visibleTenders.length} de {tenders.length} processo(s)
        </span>
        {mode === 'ONGOING' && (
          <>
            <span className="text-emerald-700 dark:text-emerald-300">{tenders.filter(isReady).length} pronto(s)</span>
            <span className="text-amber-700 dark:text-amber-300">{tenders.filter((tender) => !isReady(tender)).length} com pendências</span>
          </>
        )}
      </div>

      {visibleTenders.length === 0 ? (
        <div className="border border-dashed border-line-strong dark:border-line-dark-strong rounded-lg px-6 py-12 text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-ink-placeholder" />
          <p className="font-medium text-ink-title dark:text-ink-title-dark">
            {search ? 'Nenhuma licitação corresponde à busca.' : mode === 'FAVORITES' ? 'Nenhuma licitação em participação.' : 'Nenhuma licitação em andamento.'}
          </p>
          <p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
            {mode === 'FAVORITES' ? 'Adicione oportunidades pela Busca de oportunidade.' : 'Classifique uma licitação em participação para iniciar a preparação.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {visibleTenders.map((tender) => {
            const working = workingId === tender.id;
            const ready = isReady(tender);
            const daysRemaining = getDaysRemaining(tender.deadline);
            const category = tender.opportunityCategory || 'Não classificada';
            const contractType = classifyOpportunityContract(tender);
            const contractTypeMeta = CONTRACT_TYPE_META[contractType];
            const participationReleased = ['PARTICIPATION', 'AUCTION'].includes(String(tender.workflowStage));
            const disputeFinished = tender.auctionStatus === 'FINISHED';
            const disputeInProgress = tender.workflowStage === 'AUCTION' && !disputeFinished;
            return (
              <article key={tender.id} className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark shadow-sm overflow-hidden">
                <div className="p-4 grid xl:grid-cols-[minmax(0,1fr)_auto] gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                      <span className="font-semibold text-brand-700 dark:text-brand-300">{tender.state || 'UF não informada'}</span>
                      <span>{tender.portal || 'Portal não informado'}</span>
                      <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-semibold ${deadlineBadgeClass(daysRemaining)}`}>
                        <CalendarClock className="w-3.5 h-3.5" />
                        {deadlineLabel(daysRemaining)}
                      </span>
                      <span>Prazo: {formatDate(tender.deadline)}</span>
                      <span>{formatMoney(tender.value)}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold ${ready ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300'}`}>
                        {ready ? <CheckCircle2 className="w-3.5 h-3.5" /> : <FileCheck2 className="w-3.5 h-3.5" />}
                        {ready ? 'Preparação concluída' : 'Com pendências'}
                      </span>
                      {participationReleased && (
                        <span className="inline-flex items-center gap-1 rounded border border-emerald-400 bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white">
                          <CheckCircle2 className="w-3.5 h-3.5" />Participação liberada
                        </span>
                      )}
                      {disputeInProgress && (
                        <span className="inline-flex items-center gap-1 rounded border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-300">
                          <Gavel className="w-3.5 h-3.5" />Em disputa
                        </span>
                      )}
                      {disputeFinished && (
                        <span className="inline-flex items-center gap-1 rounded border border-emerald-400 bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white">
                          <Gavel className="w-3.5 h-3.5" />Disputa finalizada
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
                        <Tag className="w-3.5 h-3.5" />Categoria: {category}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold ${contractTypeMeta.className}`}>
                        <Tag className="w-3.5 h-3.5" />Contratação: {contractTypeMeta.label}
                      </span>
                    </div>
                    <h3 className="mt-2 text-sm font-semibold leading-5 text-ink-title dark:text-ink-title-dark">{tender.title}</h3>
                    <p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{tender.organ}</p>
                    {tender.fileUrl && (
                      <a href={tender.fileUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline">
                        <ExternalLink className="w-3.5 h-3.5" />Abrir edital
                      </a>
                    )}
                  </div>

                  <div className="xl:min-w-[480px]">
                    <p className="mb-2 text-xs font-semibold uppercase text-ink-subtitle dark:text-ink-subtitle-dark">{mode === 'FAVORITES' ? 'Qualificação de prioridade' : 'Prioridade'}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {PRIORITIES.map((option) => {
                        const Icon = option.icon;
                        const selected = tender.priority === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            title={option.label}
                            disabled={working}
                            onClick={() => void updateQualification(tender, { priority: option.value })}
                            className={`h-10 px-2 rounded-md border text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 ${
                              selected ? option.active : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt'
                            }`}
                          >
                            <Icon className="w-4 h-4 shrink-0" />
                            <span className="truncate">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => void toggleResultTracking(tender)}
                      className={`mt-2 h-10 w-full rounded-md border px-3 text-xs font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                        tender.trackResult
                          ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300'
                          : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt'
                      }`}
                    >
                      {tender.trackResult ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {tender.trackResult ? 'Resultado em acompanhamento' : 'Acompanhar resultado'}
                    </button>
                  </div>
                </div>

                {mode === 'ONGOING' && (
                  <div className="border-t border-line dark:border-line-dark bg-surface-alt/50 dark:bg-surface-dark-alt/40 px-4 py-4 grid xl:grid-cols-[220px_minmax(0,1fr)_230px] gap-4 items-start">
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase text-ink-subtitle dark:text-ink-subtitle-dark">Decisão</p>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          ['PARTICIPATE', 'Participar'],
                          ['DO_NOT_PARTICIPATE', 'Não participar'],
                        ] as Array<[ParticipationDecision, string]>).map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            disabled={working}
                            onClick={() => void updateQualification(tender, { participationDecision: value })}
                            className={`h-9 rounded-md border px-2 text-xs font-medium ${
                              tender.participationDecision === value
                                ? value === 'PARTICIPATE' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-zinc-500 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200'
                                : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase text-ink-subtitle dark:text-ink-subtitle-dark">Validação obrigatória</p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {CHECKS.map(({ field, label }) => (
                          <label key={String(field)} className="min-h-9 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2 flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={Boolean(tender[field])}
                              disabled={working}
                              onChange={(event) => void updateQualification(tender, { [field]: event.target.checked })}
                              className="w-4 h-4 accent-brand-600"
                            />
                            <span className="text-ink-text dark:text-ink-text-dark">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className={`mb-2 flex items-center gap-2 text-xs font-medium ${ready ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                        {ready ? <ShieldCheck className="w-4 h-4" /> : <FileCheck2 className="w-4 h-4" />}
                        {ready ? 'Preparação concluída' : 'Existem confirmações pendentes'}
                      </div>
                      <button
                        type="button"
                        disabled={(!ready && !participationReleased) || working}
                        onClick={() => participationReleased ? openAuctionRoom(tender) : void advance(tender)}
                        className="w-full h-10 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-45 disabled:cursor-not-allowed"
                      >
                        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : participationReleased ? <Gavel className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                        {participationReleased ? 'Abrir sala de disputa' : 'Participar e abrir sala'}
                      </button>
                      <button
                        type="button"
                        disabled={working}
                        onClick={() => void archiveTender(tender)}
                        className="mt-2 w-full h-10 rounded-md border border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark text-sm font-semibold flex items-center justify-center gap-2 hover:bg-surface-alt dark:hover:bg-surface-dark-alt disabled:opacity-45 disabled:cursor-not-allowed"
                      >
                        <Archive className="w-4 h-4" />Arquivar card
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {isManualModalOpen && mode === 'ONGOING' && (
        <div className="fixed inset-0 z-50 bg-black/50 p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-surface dark:bg-surface-dark shadow-xl flex flex-col">
            <div className="h-14 px-4 border-b border-line dark:border-line-dark flex items-center justify-between">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <Plus className="w-4 h-4 text-brand-600" />Adicionar oportunidade manual
              </h3>
              <button type="button" onClick={() => setIsManualModalOpen(false)} title="Fechar" aria-label="Fechar" className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-surface-alt dark:hover:bg-surface-dark-alt">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto grid sm:grid-cols-2 gap-4">
              <label className="sm:col-span-2 text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Objeto / título *
                <input value={manualForm.title} onChange={(event) => setManualForm((current) => ({ ...current, title: event.target.value }))} className="input w-full mt-1" placeholder="Objeto da licitação" />
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Órgão *
                <input value={manualForm.organ} onChange={(event) => setManualForm((current) => ({ ...current, organ: event.target.value }))} className="input w-full mt-1" placeholder="Órgão licitante" />
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Portal
                <input value={manualForm.portal} onChange={(event) => setManualForm((current) => ({ ...current, portal: event.target.value }))} className="input w-full mt-1" placeholder="Portal ou origem" />
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                UF
                <select value={manualForm.state} onChange={(event) => setManualForm((current) => ({ ...current, state: event.target.value }))} className="input w-full mt-1">
                  <option value="">Selecione</option>
                  {BRAZIL_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Prioridade inicial
                <select value={manualForm.priority} onChange={(event) => setManualForm((current) => ({ ...current, priority: event.target.value as TenderPriority }))} className="input w-full mt-1">
                  <option value="NONE">Sem prioridade</option>
                  <option value="PRIORITY">Prioridade</option>
                  <option value="VERY_HIGH">Altíssima prioridade</option>
                  <option value="ULTRA">Ultra prioridade</option>
                  <option value="NO_GO">Não participar</option>
                </select>
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Data de publicação
                <input
                  type="date"
                  value={manualForm.publicationDate}
                  onInput={(event) => {
                    const publicationDate = event.currentTarget.value;
                    setManualForm((current) => ({ ...current, publicationDate }));
                  }}
                  className="input w-full mt-1"
                />
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Prazo / disputa *
                <input
                  type="date"
                  value={manualForm.deadline}
                  onInput={(event) => {
                    const deadline = event.currentTarget.value;
                    setManualForm((current) => ({ ...current, deadline }));
                  }}
                  className="input w-full mt-1"
                />
              </label>
              <label className="text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Valor estimado (R$)
                <input type="number" min="0" step="0.01" value={manualForm.value} onChange={(event) => setManualForm((current) => ({ ...current, value: event.target.value }))} className="input w-full mt-1" placeholder="0,00" />
              </label>
              <label className="sm:col-span-2 text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Descrição
                <textarea value={manualForm.description} onChange={(event) => setManualForm((current) => ({ ...current, description: event.target.value }))} className="input w-full mt-1 min-h-24 resize-y" placeholder="Informações complementares" />
              </label>
            </div>
            <div className="p-4 border-t border-line dark:border-line-dark flex justify-end gap-2">
              <button type="button" onClick={() => setIsManualModalOpen(false)} className="h-10 px-4 rounded-md border border-line dark:border-line-dark text-sm font-medium">Cancelar</button>
              <button type="button" disabled={savingManual} onClick={() => void saveManualTender()} className="h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {savingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Adicionar em andamento
              </button>
            </div>
          </div>
        </div>
      )}

      {isNotificationModalOpen && mode === 'ONGOING' && (
        <div className="fixed inset-0 z-50 bg-black/50 p-4 flex items-center justify-center">
          <div className="w-full max-w-md rounded-lg bg-surface dark:bg-surface-dark shadow-xl overflow-hidden">
            <div className="h-14 px-4 border-b border-line dark:border-line-dark flex items-center justify-between">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-brand-600" />Configurar notificações
              </h3>
              <button type="button" onClick={() => setIsNotificationModalOpen(false)} title="Fechar" aria-label="Fechar" className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-surface-alt dark:hover:bg-surface-dark-alt">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark">
                Destinatários por e-mail
                <input value={notificationForm.emailRecipients} onChange={(event) => setNotificationForm((current) => ({ ...current, emailRecipients: event.target.value }))} className="input w-full mt-1" placeholder="equipe@empresa.com, gestor@empresa.com" />
              </label>
              <label className="min-h-10 rounded-md border border-line dark:border-line-dark px-3 py-2 flex items-center gap-3 text-sm">
                <input type="checkbox" checked={notificationForm.notifyDeadlineWarning} onChange={(event) => setNotificationForm((current) => ({ ...current, notifyDeadlineWarning: event.target.checked }))} className="w-4 h-4 accent-brand-600" />
                Alertar sobre prazos próximos
              </label>
              <label className="min-h-10 rounded-md border border-line dark:border-line-dark px-3 py-2 flex items-center gap-3 text-sm">
                <input type="checkbox" checked={notificationForm.notifyStatusChange} onChange={(event) => setNotificationForm((current) => ({ ...current, notifyStatusChange: event.target.checked }))} className="w-4 h-4 accent-brand-600" />
                Alertar alterações de situação e prioridade
              </label>
            </div>
            <div className="p-4 border-t border-line dark:border-line-dark flex justify-end gap-2">
              <button type="button" onClick={() => setIsNotificationModalOpen(false)} className="h-10 px-4 rounded-md border border-line dark:border-line-dark text-sm font-medium">Cancelar</button>
              <button type="button" disabled={savingNotifications} onClick={() => void saveNotificationSettings()} className="h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {savingNotifications && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar alertas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenderWorkflow;
